import { Navbar } from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { HelpCircle } from 'lucide-react';

const FAQ_ITEMS = [
  {
    category: 'Getting Started',
    questions: [
      {
        q: 'What is PDX Hacks?',
        a: 'PDX Hacks is Portland\'s hackathon community platform. We organize hackathons, workshops, and provide a space for developers to showcase projects, connect with mentors, and collaborate with others.'
      },
      {
        q: 'Do I need to be an experienced developer to join?',
        a: 'Not at all! We welcome hackers of all skill levels, from beginners to seasoned professionals. Our community is here to help everyone learn and grow.'
      },
      {
        q: 'How do I create an account?',
        a: 'Click the "Get Started" button and sign up with your email or Google account. It takes less than a minute!'
      },
    ]
  },
  {
    category: 'Projects',
    questions: [
      {
        q: 'What kind of projects can I upload?',
        a: 'Any hackathon project, personal project, or collaborative work is welcome! We encourage projects with GitHub links, live demos, and detailed descriptions.'
      },
      {
        q: 'Can I upload unfinished projects?',
        a: 'Yes! You can save projects as drafts and publish them when ready. We also have a "looking for team" feature if you need collaborators.'
      },
      {
        q: 'How do I get feedback on my project?',
        a: 'Post your project and share it in the forum\'s #feedback category. The community is always happy to provide constructive feedback.'
      },
    ]
  },
  {
    category: 'Forum & Community',
    questions: [
      {
        q: 'How does the reputation system work?',
        a: 'You earn reputation points by posting, commenting, having your answers marked as solutions, and receiving upvotes. Higher reputation unlocks badges and shows your community contribution.'
      },
      {
        q: 'What are the forum categories?',
        a: 'We have General, Help & Support, Showcase, Jobs, Events, Feedback, Resources, and Portland Local categories to help organize discussions.'
      },
      {
        q: 'How do I become a mentor?',
        a: 'Active community members with high reputation and relevant experience can apply to become mentors. Contact us through the Contact page for more info.'
      },
    ]
  },
  {
    category: 'Events',
    questions: [
      {
        q: 'How do I register for an event?',
        a: 'Navigate to the Events page, find an event you\'re interested in, and click "Register". You\'ll need an account to register.'
      },
      {
        q: 'Are events in-person or virtual?',
        a: 'We host both! Each event listing clearly indicates whether it\'s in-person, virtual, or hybrid.'
      },
      {
        q: 'Can I organize an event?',
        a: 'Community members can propose events. Reach out to us through the Contact page to discuss organizing a workshop or meetup.'
      },
    ]
  },
];

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="faq-page">
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="w-6 h-6 text-indigo-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-slate-600">
            Find answers to common questions about PDX Hacks
          </p>
        </div>

        <div className="space-y-8">
          {FAQ_ITEMS.map((section) => (
            <Card key={section.category}>
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">{section.category}</h2>
                <Accordion type="single" collapsible className="space-y-2">
                  {section.questions.map((item, index) => (
                    <AccordionItem key={index} value={`${section.category}-${index}`} className="border-none">
                      <AccordionTrigger className="text-left hover:no-underline py-3 px-4 bg-slate-50 rounded-lg hover:bg-slate-100">
                        <span className="font-medium text-slate-900">{item.q}</span>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pt-2 pb-4 text-slate-600">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-slate-600">
            Still have questions? {' '}
            <a href="/contact" className="text-indigo-600 font-medium hover:text-indigo-800">
              Contact us
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
